import React, { useState, useEffect, useMemo } from "react";
import {
  InfoCard,
  InfoCardContent,
  InfoCardTitle,
  InfoCardDescription,
  InfoCardMedia,
  InfoCardFooter,
  InfoCardDismiss,
} from "@/components/ui/info-card";
import { useSidebar } from "@/components/ui/sidebar";
import { motion, AnimatePresence, PanInfo } from "framer-motion";
import { cn } from "@/lib/utils";
import { Kbd, KbdGroup } from "@/components/ui/kbd";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface UpdateCard {
  id: string;
  storageKey: string;
  title: string;
  description: string | React.ReactNode;
  media?: {
    src: string;
    alt: string;
  }[];
  shrinkHeight?: number;
  expandHeight?: number;
}

interface NavInfoCardStackProps {
  updates: UpdateCard[];
}

export function NavInfoCardStack({ updates }: NavInfoCardStackProps) {
  const { state } = useSidebar();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<"left" | "right">("right");
  const [isDragging, setIsDragging] = useState(false);
  const [previewHeight, setPreviewHeight] = useState<number | null>(null);
  const [dismissTrigger, setDismissTrigger] = useState(0); // Trigger re-computation
  const cardRef = React.useRef<HTMLDivElement>(null);
  
  // Store stable preview card data that won't change mid-animation
  const [previewCardData, setPreviewCardData] = React.useState<{
    next: UpdateCard | null;
    prev: UpdateCard | null;
  }>({ next: null, prev: null });

  // Filter out dismissed cards
  const activeUpdates = useMemo(() => {
    return updates.filter((update) => {
      if (typeof window === "undefined") return true;
      return localStorage.getItem(update.storageKey) !== "dismissed";
    });
  }, [updates, dismissTrigger]); // Re-compute when dismissTrigger changes

  // Ensure currentIndex is valid
  useEffect(() => {
    if (currentIndex >= activeUpdates.length && activeUpdates.length > 0) {
      setCurrentIndex(Math.max(0, activeUpdates.length - 1));
    }
  }, [activeUpdates.length, currentIndex]);

  // Hide card when sidebar is collapsed
  if (state === "collapsed") {
    return null;
  }

  // If no active updates, don't render anything
  if (activeUpdates.length === 0) {
    return null;
  }

  // Safety check: ensure currentIndex is valid
  const safeIndex = Math.min(currentIndex, activeUpdates.length - 1);
  const currentUpdate = activeUpdates[safeIndex];
  
  // If currentUpdate is undefined, something went wrong
  if (!currentUpdate) {
    return null;
  }
  
  const hasMultiple = activeUpdates.length > 1;
  const remainingCount = activeUpdates.length - safeIndex - 1;

  const handleDismiss = () => {
    // Use setTimeout to ensure localStorage write completes first
    setTimeout(() => {
      // Trigger re-computation of activeUpdates after localStorage changes
      setDismissTrigger((prev) => prev + 1);
      
      // After dismissal, the activeUpdates array will shrink by 1
      const newLength = activeUpdates.length - 1;
      
      if (newLength === 0) {
        // No more cards, nothing to do
        return;
      }
      
      // If we're dismissing the last card, move to the previous one
      if (currentIndex >= newLength) {
        setDirection("left");
        setCurrentIndex(newLength - 1);
      }
      // Otherwise, the same index will now point to the next card
      // (because the current card will be removed from the array)
      // No need to change currentIndex
    }, 0);
  };

  const handlePrevious = () => {
    setDirection("left");
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      setCurrentIndex(activeUpdates.length - 1);
    }
  };

  const handleNext = () => {
    setDirection("right");
    if (currentIndex < activeUpdates.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
    }
  };

  // Capture card height and set dragging state
  const handleDragStart = () => {
    if (cardRef.current) {
      setPreviewHeight(cardRef.current.offsetHeight);
    }
    
    // Capture next and previous cards at drag start - these won't change during drag
    const nextIndex = (safeIndex + 1) % activeUpdates.length;
    const prevIndex = safeIndex === 0 ? activeUpdates.length - 1 : safeIndex - 1;
    setPreviewCardData({
      next: activeUpdates[nextIndex],
      prev: activeUpdates[prevIndex],
    });
    
    setIsDragging(true);
  };

  // Handle drag to update direction
  const handleDrag = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    // Update direction based on drag offset
    if (info.offset.x < -10) {
      setDirection("right"); // Dragging left -> next card
    } else if (info.offset.x > 10) {
      setDirection("left"); // Dragging right -> previous card
    }
  };

  // Handle drag end for swipe navigation
  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const swipeThreshold = 20; // Very low threshold - easy to trigger
    const swipeVelocity = 200; // Low velocity threshold

    // Check if user swiped with enough distance or velocity
    const significantSwipe = 
      Math.abs(info.offset.x) > swipeThreshold || 
      Math.abs(info.velocity.x) > swipeVelocity;

    if (significantSwipe) {
      if (info.offset.x > 0) {
        // Swiped right -> go to previous (with infinite loop)
        if (currentIndex > 0) {
          setCurrentIndex(currentIndex - 1);
        } else {
          // Loop to last card
          setCurrentIndex(activeUpdates.length - 1);
        }
      } else if (info.offset.x < 0) {
        // Swiped left -> go to next (with infinite loop)
        if (currentIndex < activeUpdates.length - 1) {
          setCurrentIndex(currentIndex + 1);
        } else {
          // Loop to first card
          setCurrentIndex(0);
        }
      }
    }
    
    setIsDragging(false);
    setPreviewHeight(null);
  };

  // Render hidden first, then show
  const slideVariants = {
    enter: {
      opacity: 0,
    },
    center: {
      opacity: 1,
    },
    exit: {
      opacity: 0,
      display: 'none' as const,
    },
  };

  return (
    <div className={cn(
      "relative",
      // Add extra bottom margin when stacked to accommodate visible stack layers
      hasMultiple ? "mb-3" : "mb-0"
    )}>
      {/* Stacked cards background (visual only) - MORE VISIBLE */}
      {hasMultiple && (
        <>
          {/* Bottom-left stack layer (furthest back) */}
          <div
            className="absolute -bottom-3 -left-3 right-3 h-full rounded-lg border border-border bg-card shadow-md pointer-events-none"
            style={{
              transform: "scale(0.94)",
            }}
          />
          {/* Middle stack layer */}
          <div
            className="absolute -bottom-1.5 -left-1.5 right-1.5 h-full rounded-lg border border-border bg-card shadow-md pointer-events-none"
            style={{
              transform: "scale(0.97)",
            }}
          />
        </>
      )}

      {/* Main active card with preview cards */}
      <div className="relative z-10 isolate">
        {/* Preview card layer - Removed to prevent blank card flash */}

        {/* Main current card - All cards rendered, only current visible */}
        <div className="relative">
          {activeUpdates.map((update, index) => (
            <motion.div
              key={update.id}
              ref={index === safeIndex ? cardRef : null}
              drag={hasMultiple && index === safeIndex ? "x" : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.3}
              dragTransition={{ bounceStiffness: 700, bounceDamping: 25 }}
              dragMomentum={true}
              onDragStart={index === safeIndex ? handleDragStart : undefined}
              onDrag={index === safeIndex ? handleDrag : undefined}
              onDragEnd={index === safeIndex ? handleDragEnd : undefined}
              className={cn(
                "w-full transition-opacity duration-150",
                index === safeIndex ? "opacity-100 cursor-grab active:cursor-grabbing select-none" : "opacity-0 absolute top-0 left-0 pointer-events-none",
                isDragging && index === safeIndex && "cursor-grabbing"
              )}
              whileTap={index === safeIndex ? { cursor: "grabbing" } : undefined}
            >
              <InfoCard
                storageKey={update.storageKey}
                dismissType="forever"
              >
                <InfoCardContent className="pb-0">
                  {/* Title */}
                  <InfoCardTitle className="text-sm font-semibold">
                    {update.title}
                  </InfoCardTitle>

                  {/* Description */}
                  <InfoCardDescription className="text-xs">
                    {update.description}
                  </InfoCardDescription>

                  {/* Media (if provided) */}
                  {update.media && update.media.length > 0 && (
                    <InfoCardMedia
                      media={update.media}
                      shrinkHeight={update.shrinkHeight || 65}
                      expandHeight={update.expandHeight || 110}
                      loading="eager"
                    />
                  )}

                {/* Footer with dismiss and navigation */}
                <InfoCardFooter className="mt-0 pt-1">
                  <div className="flex items-center justify-between w-full">
                    {/* Left: Dismiss button */}
                    <InfoCardDismiss onDismiss={handleDismiss}>
                      Dismiss
                    </InfoCardDismiss>
                    
                    {/* Right: Navigation controls */}
                    {hasMultiple && (
                      <div className="flex items-center gap-3">
                        {/* Arrows */}
                        <div className="flex items-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={handlePrevious}
                            className="h-6 w-6 hover:bg-accent"
                            aria-label="Previous"
                          >
                            <ChevronLeft className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleNext}
                            className="h-6 w-6 hover:bg-accent"
                            aria-label="Next"
                          >
                            <ChevronRight className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        
                        {/* Page indicator dots */}
                        <div className="flex items-center gap-1">
                          {activeUpdates.map((_, index) => (
                            <button
                              key={index}
                              onClick={() => {
                                setDirection(index > safeIndex ? "right" : "left");
                                setCurrentIndex(index);
                              }}
                              className={cn(
                                "h-1.5 rounded-full transition-all",
                                index === safeIndex
                                  ? "w-4 bg-primary"
                                  : "w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                              )}
                              aria-label={`Go to update ${index + 1}`}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </InfoCardFooter>
              </InfoCardContent>
            </InfoCard>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

